import React, { useEffect, useRef, useState } from 'react';
import { AdoptionBody, AdoptionPreview, AdoptionResult, CatalogueRef, applyAdoption, previewAdoption, errorMessage } from '../../api/emailCatalogue';
import { getWorkflow, getWorkflowVersion, getAuthoringCatalogue } from '../../api/cafWorkflows';
import { getAutomationWorkflow } from '../../api/cafAutomation';
import { AuthoringCatalogue, WorkflowDefinition, isProtectedKind } from '../caf-workflows/workflowTypes';
import CatalogueSelect from './CatalogueSelect';
import { ConfigValues } from './EmailSettingsReview';

type Occurrence = { key: string; family: string; purpose: string };
function occurrences(definition: WorkflowDefinition, catalogue: AuthoringCatalogue): Occurrence[] {
  return definition.nodes.flatMap(node => {
    const config = node.config;
    if (node.kind === 'send_email') {
      const owner = definition.nodes.find(item => item.id === config.reminder_for_node_id)?.kind;
      return [{ key: node.id + '/email', family: owner === 'multi_rep' ? 'multi_rep_reminder' : owner && isProtectedKind(owner) ? owner : 'generic', purpose: 'Email' }];
    }
    if (isProtectedKind(node.kind)) return [
      { key: node.id + '/email', family: node.kind, purpose: 'Client email' },
      ...(config.chaser ? [{ key: node.id + '/reminder', family: 'multi_rep_reminder', purpose: 'Reminder' }] : []),
    ];
    if (node.kind === 'cancel_claims') return (catalogue.cancellation.reasons.find(item => item.value === config.reason)?.roles || [])
      .map(role => ({ key: node.id + '/' + role.role, family: role.role, purpose: role.purpose }));
    return [];
  });
}

export default function WorkflowEmailAdoptionReview({ workflowId, version, initialSelections = {}, onApplied, onClose, onApplyStart,
  readOnly = false, workflowState, unsavedChanges = false }: {
  workflowId: string; version: number | null; initialSelections?: Record<string, CatalogueRef>;
  onApplied: (result: AdoptionResult) => void | Promise<void>;
  /** Acquire the parent mutation fence; release after Apply and its refresh settle. */
  onApplyStart?: () => (() => void) | null;
  onClose: () => void; readOnly?: boolean;
  workflowState?: string | null; unsavedChanges?: boolean;
}) {
  const [selection, setSelection] = useState(initialSelections), [note, setNote] = useState('');
  const [loaded, setLoaded] = useState<{ revision: number; automation_revision: number | null; state: string | null;
    catalogue: AuthoringCatalogue; occurrences: Occurrence[] } | null>(null);
  const [preview, setPreview] = useState<AdoptionPreview | null>(null);
  const [problem, setProblem] = useState(''), [busy, setBusy] = useState(false), [refresh, setRefresh] = useState(false);
  const [applying, setApplying] = useState(false);
  const sequence = useRef(0), busyOwner = useRef<number | null>(null);
  const begin = () => { if (busyOwner.current !== null) return null; const owner = ++sequence.current;
    busyOwner.current = owner; setBusy(true); return owner; };
  const owns = (owner: number) => sequence.current === owner && busyOwner.current === owner;
  const finish = (owner: number) => { if (owns(owner)) { busyOwner.current = null; setBusy(false); } };
  const load = async () => {
    const owner = begin(); if (owner === null) return;
    setPreview(null); setProblem('');
    try {
      const [head, automation, catalogue] = await Promise.all([getWorkflow(workflowId),
        getAutomationWorkflow(workflowId).catch(error => {
          if ((error as { response?: { status?: number } }).response?.status === 404) return null;
          throw error;
        }), getAuthoringCatalogue()]);
      const definition = version === null ? head.definition : (await getWorkflowVersion(workflowId, version)).definition;
      if (!owns(owner)) return;
      if (!definition) throw new Error('Workflow source unavailable');
      setLoaded({ revision: head.revision, automation_revision: automation?.revision ?? null, state: automation?.state ?? null,
        catalogue, occurrences: occurrences(definition, catalogue) });
      setRefresh(false);
    } catch (error) { if (owns(owner)) { setProblem(errorMessage(error)); setRefresh(true); } }
    finally { finish(owner); }
  };
  useEffect(() => {
    sequence.current += 1; busyOwner.current = null; setBusy(false); setLoaded(null); setPreview(null);
    setApplying(false); setSelection(initialSelections); setNote(''); setProblem(''); setRefresh(false);
    if (!readOnly) void load();
    return () => { sequence.current += 1; busyOwner.current = null; };
    // Identity owns every response. Ref changes are chosen in the form, never silently adopted from props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, version, readOnly]);
  const body = (): AdoptionBody => ({ workflow_id: workflowId, version, expected_revision: loaded!.revision,
    expected_automation_revision: loaded!.automation_revision, selections: selection });
  const inspect = async () => {
    if (!loaded || refresh || readOnly || unsavedChanges) return;
    const owner = begin(); if (owner === null) return;
    setProblem(''); setPreview(null);
    try { const result = await previewAdoption(body()); if (owns(owner)) setPreview(result); }
    catch (error) { if (owns(owner)) { setProblem(errorMessage(error)); setRefresh(true); } }
    finally { finish(owner); }
  };
  const apply = async () => {
    if (!loaded || !preview || preview.blockers.length || refresh || readOnly || unsavedChanges) return;
    const owner = begin(); if (owner === null) return;
    const release = onApplyStart ? onApplyStart() : () => {};
    if (!release) { finish(owner); return; }
    setApplying(true); setProblem('');
    try {
      const result = await applyAdoption({ ...body(), preview_hash: preview.preview_hash, ...(note.trim() ? { note: note.trim() } : {}) });
      if (owns(owner)) await onApplied(result);
    } catch (error) { if (owns(owner)) { setProblem(errorMessage(error)); setPreview(null); setRefresh(true); } }
    finally { release(); if (owns(owner)) setApplying(false); finish(owner); }
  };
  return <section className="ec-review" aria-label="Adopt workflow email settings">
    <h2>Review workflow email changes</h2>
    <p>Source: {version === null ? 'Draft only' : `version ${version}`} · Workflow: {workflowId}</p>
    <p>Workflow remains {preview?.state ?? loaded?.state ?? workflowState ?? 'unmanaged'}. Existing pauses, reminder policies, example links and pattern pins are preserved.</p>
    <p>Already admitted runs keep their frozen settings.</p>
    {readOnly ? <p>You have read-only access to these email settings.</p> : <>
      {unsavedChanges && <p role="alert">Keep or discard the unsaved workflow edits before adopting email settings. Your local edits have been preserved.</p>}
      {problem && <p role="alert">{problem} Your selections and note are preserved. Refresh the workflow revisions and preview again.</p>}
      {!loaded && busy && <p role="status">Loading workflow revisions…</p>}
      {loaded && <>
        <p>Definition revision {loaded.revision} · Automation revision {loaded.automation_revision ?? 'none (unmanaged)'}</p>
        {loaded.catalogue.catalogue_blockers?.map(code => <p role="alert" key={code}>{code.replace(/_/g, ' ')}</p>)}
        {loaded.occurrences.map(item => <section key={item.key}><h3>{item.purpose}</h3>
          <CatalogueSelect label={`Email settings for ${item.key}`} family={item.family} value={selection[item.key]}
            options={loaded.catalogue.catalogue_options} nextCursor={loaded.catalogue.catalogue_next_cursor} disabled={busy || unsavedChanges}
            onChange={ref => { setSelection(current => ({ ...current, [item.key]: ref })); setPreview(null); }} />
          {selection[item.key] && <button type="button" disabled={busy} onClick={() => { setSelection(current => {
            const next = { ...current }; delete next[item.key]; return next; }); setPreview(null); }}>Leave this occurrence unchanged</button>}
        </section>)}
        {!loaded.occurrences.length && <p>No supported email occurrences were found in this source.</p>}
        <label>Adoption note (optional)<textarea value={note} maxLength={2000} disabled={busy} onChange={event => setNote(event.target.value)} /></label>
        <button type="button" disabled={busy || refresh || unsavedChanges || !Object.keys(selection).length}
          onClick={() => { void inspect(); }}>Preview selected email changes</button>
      </>}
      {refresh && <button type="button" disabled={busy} onClick={() => { void load(); }}>Refresh workflow revisions</button>}
      {preview && <section aria-label="Reviewed adoption changes">
        <p>Observed global mode: {preview.global_mode.mode}. This observation is informational; adoption does not change the switch.</p>
        {preview.blockers.length > 0 && <ul role="alert">{preview.blockers.map((blocker, index) => <li key={index}>
          {blocker.code.replace(/_/g, ' ')} · {blocker.path}{blocker.action ? ` · ${blocker.action.replace(/_/g, ' ')}` : ''}</li>)}</ul>}
        {preview.changes.map(change => <section key={change.occurrence}><h3>{change.occurrence}</h3>
          <div className="ec-comparison"><section><h4>Before</h4>
            {change.before.option && <ConfigValues config={change.before.option.config} />}
            {change.before.subject_evidence.availability === 'not_recorded' ? <p>Previous subject was not recorded; it is unavailable.</p>
              : <p>Previous subject: {change.before.subject_evidence.value}</p>}
            <details><summary>Original node and recorded source evidence</summary><pre>{JSON.stringify({ node: change.before.node_config,
              email: change.before.email, source: change.before.source_evidence }, null, 2)}</pre></details>
          </section><section><h4>After</h4><ConfigValues config={change.after.option.config} />
            <p>Option {change.after.option.ref.option_id} · revision {change.after.option.ref.revision}</p>
            <details><summary>Selected provider evidence and node</summary><pre>{JSON.stringify({ provider: change.after.option.provider,
              node: change.after.node_config }, null, 2)}</pre></details></section></div>
        </section>)}
        <button type="button" disabled={busy || refresh || unsavedChanges || !!preview.blockers.length || !preview.changes.length}
          onClick={() => { void apply(); }}>Apply reviewed email changes</button>
      </section>}
    </>}
    <button type="button" disabled={applying} onClick={onClose}>Close email review</button>
  </section>;
}
